import axios, { AxiosError, AxiosInstance } from 'axios';
import { installAuthInterceptors } from '@/lib/auth/axios-auth';
import type {
  ClaimRemark,
  VehicleClaimCreateRequest,
  VehicleClaimCreateResponse,
  VehicleClaimListFilters,
  VehicleClaimListResponse,
  VehicleClaimStatsResponse,
} from '@/types/vehicle-claim';
import type { ClaimMediaConfig, MediaRecord } from '@/components/vehicle-claims/media/types';
import type { RequestContext } from '@/components/vehicle-claims/media/requestContext';

const BASE_PATH = '/api/v1/insurance/vehicle-claims';
const API_URL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8000';

function buildClient(): AxiosInstance {
  const c = axios.create({
    baseURL: API_URL,
    headers: { 'Content-Type': 'application/json' },
    timeout: 30_000,
  });
  installAuthInterceptors(c);
  return c;
}

let _client: AxiosInstance | null = null;
function client(): AxiosInstance {
  if (!_client) _client = buildClient();
  return _client;
}

// ── Error wrapper ────────────────────────────────────────────────────────────

export interface ApiFieldError { field: string; message: string; }

export class VehicleClaimApiError extends Error {
  readonly status: number;
  readonly code?: string;
  readonly fieldErrors: ApiFieldError[];

  constructor(message: string, status: number, code?: string, fieldErrors: ApiFieldError[] = []) {
    super(message);
    this.name = 'VehicleClaimApiError';
    this.status = status;
    this.code = code;
    this.fieldErrors = fieldErrors;
  }
}

function wrap(err: unknown): VehicleClaimApiError {
  if (err instanceof AxiosError && err.response) {
    const { status, data } = err.response as { status: number; data: any };
    const message =
      data?.error?.message || data?.message || data?.detail || err.message || 'Request failed';
    const code = data?.error?.code || data?.code;
    return new VehicleClaimApiError(message, status, code);
  }
  return new VehicleClaimApiError(
    err instanceof Error ? err.message : 'Unknown error',
    0,
  );
}

// `pinnedTenantId` lets a caller that already knows which tenant a specific
// claim belongs to (from that claim's own GET response) force the header to
// match it, instead of re-reading whatever tenant_id currently sits in
// localStorage. Without this, editing a claim across a tenant switch that
// happens elsewhere (another tab, a tenant-switcher click) silently sends
// the PATCH/remark under the new tenant and 404s against the claim's real
// tenant — see 2026-08-28 production incident on ClaimDetailsTab Save.
function tenantHeaders(pinnedTenantId?: string): Record<string, string> {
  if (typeof window === 'undefined') return {};
  const tenantId = pinnedTenantId || localStorage.getItem('tenant_id') || '';
  const requestId = crypto.randomUUID();
  return {
    'X-Tenant-Id': tenantId,
    'X-Request-Id': requestId,
  };
}

// ── API functions ────────────────────────────────────────────────────────────

export async function createVehicleClaim(
  payload: VehicleClaimCreateRequest,
): Promise<VehicleClaimCreateResponse> {
  try {
    const res = await client().post<VehicleClaimCreateResponse>(BASE_PATH, payload, {
      headers: tenantHeaders(),
    });
    return res.data;
  } catch (err) {
    throw wrap(err);
  }
}

export interface ShareLinkResponse {
  token: string;
  url: string;
  vehicle_registration_no: string | null;
  claim_number: string | null;
  report_number: string | null;
  customer_name: string | null;
  customer_mobile: string | null;
  tenant_name: string | null;
  garage_name: string | null;
  garage_address: string | null;
  garage_mobile: string | null;
}

export async function createShareLink(jobId: string): Promise<ShareLinkResponse> {
  try {
    const res = await client().post<ShareLinkResponse>(`${BASE_PATH}/${jobId}/share-link`, undefined, {
      headers: tenantHeaders(),
    });
    return res.data;
  } catch (err) {
    throw wrap(err);
  }
}

// The public portal page (apps/web/src/app/claim-portal/[token]/page.tsx)
// receives only a share token in its own URL; every other claim API call is
// job_id-scoped, so this must run first to bootstrap the real job_id. Unlike
// every other function here, it is unauthenticated (no Authorization header,
// no tenantHeaders()) — the token itself is the credential, sent via the
// X-Share-Token header only (never a URL path/query segment, which risks
// the token landing in proxy/access logs or browser history — T23).
export async function resolvePortalToken(token: string): Promise<{ job_id: string; status_code: string | null }> {
  try {
    const res = await axios.get<{ job_id: string; status_code: string | null }>(`${API_URL}${BASE_PATH}/portal-token`, {
      headers: { 'X-Share-Token': token },
    });
    return res.data;
  } catch (err) {
    throw wrap(err);
  }
}

export async function listVehicleClaims(
  params: VehicleClaimListFilters & { limit: number; offset: number },
): Promise<VehicleClaimListResponse> {
  try {
    const res = await client().get<VehicleClaimListResponse>(BASE_PATH, {
      params,
      headers: tenantHeaders(),
    });
    return res.data;
  } catch (err) {
    throw wrap(err);
  }
}

export interface VehicleTypeOption {
  id: number;
  name: string;
  code: string;
}

export async function listVehicleTypes(): Promise<VehicleTypeOption[]> {
  try {
    const res = await client().get<VehicleTypeOption[]>(`${BASE_PATH}/vehicle-types/lookup`, {
      headers: tenantHeaders(),
    });
    return res.data;
  } catch (err) {
    throw wrap(err);
  }
}

export async function getVehicleClaimStats(
  filters: VehicleClaimListFilters = {},
): Promise<VehicleClaimStatsResponse> {
  try {
    const res = await client().get<VehicleClaimStatsResponse>(`${BASE_PATH}/stats`, {
      params: filters,
      headers: tenantHeaders(),
    });
    return res.data;
  } catch (err) {
    throw wrap(err);
  }
}

export async function exportVehicleClaims(filters: VehicleClaimListFilters): Promise<Blob> {
  try {
    const res = await client().get<Blob>(`${BASE_PATH}/export`, {
      params: filters,
      headers: tenantHeaders(),
      responseType: 'blob',
    });
    return res.data;
  } catch (err) {
    throw wrap(err);
  }
}

export async function getVehicleClaim(jobId: string): Promise<unknown> {
  try {
    const res = await client().get(`${BASE_PATH}/${jobId}`, {
      headers: tenantHeaders(),
    });
    return res.data;
  } catch (err) {
    throw wrap(err);
  }
}

export interface UiicSummary {
  job_id: string;
  job_number: string;
  report_number: string | null;
  claim_number: string;
  policy_no: string;
  vehicle_registration_no: string;
  customer_name: string | null;
  type_of_settlement: string;
  date_of_survey: string | null;
  time_of_survey: string | null;
  place_of_survey: string | null;
  payment_option: string | null;
  estimate_total_amount: number | null;
  initial_loss_assessment_amount: number | null;
  vehicle_age_based_depreciation_amount: number;
  fifty_percent_depreciation_amount: number;
  thirty_percent_depreciation_amount: number;
  nil_depreciation_amount: number;
  net_assessed_parts_amount_with_gst: number;
  net_assessed_parts_amount_zero_gst: number;
  labour_charges_with_gst: number;
  labour_charges_zero_gst: number;
  total_labour_charges_excluding_gst: number;
  surveyors_observation: string;
  first_remarks: string;
  towing_charges: number | null;
  voluntary_excess: number | null;
  compulsory_excess: number | null;
  salvage_value: number | null;
  net_assessed_amount: number | null;
  net_assessed_amount_including_gst: number | null;
  final_report_number: string;
  invoice_number: string | null;
  invoice_date: string | null;
  workshop_invoice_no: string | null;
  workshop_invoice_date: string | null;
  traveling_expenses: number | null;
  documents: {
    pan_card_path: string | null;
    aadhaar_card_path: string | null;
    cancelled_cheque_path: string | null;
    claim_form_path: string | null;
    satisfaction_voucher_path: string | null;
    photo_front_path: string | null;
    photo_rear_path: string | null;
    photo_left_path: string | null;
    photo_right_path: string | null;
    work_order_url: string | null;
    all_photos_pdf_path: string;
    dl_combined_path: string;
    rc_combined_path: string;
    estimate_combined_path: string;
    repair_invoice_combined_path: string;
    survey_report_combined_path: string;
  };
}

export async function getUiicSummary(jobId: string): Promise<UiicSummary> {
  try {
    const res = await client().get<UiicSummary>(`${BASE_PATH}/${jobId}/uiic-summary`, {
      headers: tenantHeaders(),
    });
    return res.data;
  } catch (err) {
    throw wrap(err);
  }
}

// UIIC Automated Portal Push -- manual "GC Portal Push" trigger + the
// attempt log it and the 5-minute automatic poller both write to
// (services/insurance-service/app/domain/uiic_portal_push/push_claim.py,
// app/jobs/uiic_portal_push_poller.py). Shape mirrors
// job_schema.uiic_portal_push_log's columns directly.

export type UiicPushStatus =
  | 'pending'
  | 'in_progress'
  | 'data_push_success'
  | 'final_submit_success'
  | 'failed'
  | 'retry_required'
  | 'completed';

// One problem found by the pre-flight check (nothing was sent to UIIC) -- where to fix it and what to do.
export interface UiicPushIssue {
  code: string;
  severity: string;
  field_label: string;
  where_to_fix: string;
  message: string;
  remedy: string;
}

// What a failed push means, in plain language -- derived server-side from the recorded error text by
// job_schema.uiic_push_error_rules (see error_explainer.py). Null for attempts that did not fail, or if
// the rules are unavailable (the raw error_message is always still present).
export interface UiicPushExplanation {
  category: string;
  category_name: string;
  headline: string;
  field_label: string | null;
  where_to_fix: string | null;
  remedy: string;
  // true = pushing again can succeed; false = retrying will not help; null = unknown
  retryable: boolean | null;
  rule_code: string | null;
  // e.g. "Step: Interim Report Submit · waited 60.0 s · 17 documents (12.4 MB)"
  detail: string;
  // Pre-flight problems, when the push was blocked before anything was sent. Absent on older log rows.
  issues?: UiicPushIssue[];
}

export interface UiicPushLogEntry {
  id: string;
  push_type: 'automatic' | 'manual';
  status: UiicPushStatus;
  attempt_count: number;
  started_at: string;
  last_attempt_at: string;
  completed_at: string | null;
  data_push_response: Record<string, unknown> | null;
  interim_submit_response: Record<string, unknown> | null;
  final_submit_response: Record<string, unknown> | null;
  error_message: string | null;
  error_details: Record<string, unknown> | null;
  explanation: UiicPushExplanation | null;
  created_by: string | null;
}

export interface UiicPushResult {
  status: UiicPushStatus;
  message: string;
  log_id: string;
  error_message: string | null;
}

export async function triggerUiicPortalPush(jobId: string, options?: { skipInterim?: boolean }): Promise<UiicPushResult> {
  try {
    const res = await client().post<UiicPushResult>(
      `${BASE_PATH}/${jobId}/uiic-push`,
      {},
      { headers: tenantHeaders(), params: options?.skipInterim ? { skip_interim: true } : undefined },
    );
    return res.data;
  } catch (err) {
    throw wrap(err);
  }
}

export async function getUiicPortalPushLog(jobId: string): Promise<UiicPushLogEntry[]> {
  try {
    const res = await client().get<UiicPushLogEntry[]>(`${BASE_PATH}/${jobId}/uiic-push-log`, {
      headers: tenantHeaders(),
    });
    return res.data;
  } catch (err) {
    throw wrap(err);
  }
}

// Claim Status History -- every job.status_id change for this claim, newest
// first. Backed by job_schema.job_status_history (GET .../status-history,
// services/insurance-service/app/api/v1/vehicle_claims.py's
// list_claim_status_history), written at every status-change site by
// record_status_change() (app/domain/claim_status_history.py).

export interface ClaimStatusHistoryEntry {
  id: string;
  changed_at: string;
  from_status_code: string | null;
  from_status_name: string | null;
  to_status_code: string;
  to_status_name: string;
  changed_by_email: string | null;
  changed_by_name: string | null;
  notes: string | null;
}

export async function getClaimStatusHistory(jobId: string): Promise<ClaimStatusHistoryEntry[]> {
  try {
    const res = await client().get<ClaimStatusHistoryEntry[]>(`${BASE_PATH}/${jobId}/status-history`, {
      headers: tenantHeaders(),
    });
    return res.data;
  } catch (err) {
    throw wrap(err);
  }
}

// Claim Activity Log -- append-only audit trail of every claim mutation
// (documents, photos, videos, status changes, assessment edits). Backed by
// job_schema.claim_activity_log (GET .../activity-log,
// services/insurance-service/app/api/v1/vehicle_claims.py's
// list_claim_activity_log), written at every mutation site by
// log_claim_activity() (app/domain/claim_activity_log.py) -- no
// update/delete path exists for this table anywhere in the app.

export interface ClaimActivityLogEntry {
  id: string;
  event_type: string;
  actor_label: string | null;
  details: Record<string, unknown> | null;
  created_at: string;
}

export async function getClaimActivityLog(jobId: string): Promise<ClaimActivityLogEntry[]> {
  try {
    const res = await client().get<ClaimActivityLogEntry[]>(`${BASE_PATH}/${jobId}/activity-log`, {
      headers: tenantHeaders(),
    });
    return res.data;
  } catch (err) {
    throw wrap(err);
  }
}

// Fetches one of this page's backend-proxied document/photo routes (path
// comes straight from getUiicSummary's response, e.g.
// documents.dl_combined_path or documents.pan_card_path) as a blob --
// every one of them needs an authenticated request and returns the file
// already compressed to fit UIIC's 2MB upload limit. Same long-timeout
// treatment as the Bulk Downloads below (LONG_DOWNLOAD_TIMEOUT_MS) --
// combining/compressing every photo on a claim with many uploads can run
// past the shared client()'s normal 30s timeout.
export async function getUiicDocumentPdf(path: string): Promise<Blob> {
  try {
    const res = await client().get<Blob>(path, {
      headers: tenantHeaders(),
      responseType: 'blob',
      timeout: LONG_DOWNLOAD_TIMEOUT_MS,
    });
    return res.data;
  } catch (err) {
    throw wrap(err);
  }
}

export async function updateVehicleClaim(
  jobId: string,
  payload: Record<string, unknown>,
  tenantId?: string,
): Promise<{ message: string; job_id: string }> {
  try {
    const res = await client().patch(`${BASE_PATH}/${jobId}`, payload, {
      headers: tenantHeaders(tenantId),
    });
    return res.data;
  } catch (err) {
    throw wrap(err);
  }
}

export async function listClaimRemarks(jobId: string, tenantId?: string): Promise<ClaimRemark[]> {
  try {
    const res = await client().get<ClaimRemark[]>(`${BASE_PATH}/${jobId}/remarks`, {
      headers: tenantHeaders(tenantId),
    });
    return res.data;
  } catch (err) {
    throw wrap(err);
  }
}

export async function addClaimRemark(
  jobId: string,
  remarkText: string,
  tenantId?: string,
): Promise<ClaimRemark> {
  try {
    const res = await client().post<ClaimRemark>(
      `${BASE_PATH}/${jobId}/remarks`,
      { remark_text: remarkText },
      { headers: tenantHeaders(tenantId) },
    );
    return res.data;
  } catch (err) {
    throw wrap(err);
  }
}

// ── Claim media (photos/videos) ─────────────────────────────────────────────
// Matches the endpoints on services/insurance-service/app/api/v1/claim_media.py:
//   GET/POST  {BASE_PATH}/{jobId}/photos       DELETE {BASE_PATH}/{jobId}/photos/{photoId}
//   GET/POST  {BASE_PATH}/{jobId}/videos       DELETE {BASE_PATH}/{jobId}/videos/{videoId}
//
// The media-config endpoint below matches
// services/insurance-service/app/api/v1/claim_media_config.py — it is
// tenant-wide (not job-scoped), so its path has no {jobId} segment.

export async function getClaimMediaConfig(context?: RequestContext): Promise<ClaimMediaConfig> {
  try {
    if (context) {
      const res = await axios.get<ClaimMediaConfig>(`${context.baseUrl}/media-config`, {
        headers: context.headers,
      });
      return res.data;
    }
    const res = await client().get<ClaimMediaConfig>(`${BASE_PATH}/media-config`, {
      headers: tenantHeaders(),
    });
    return res.data;
  } catch (err) {
    throw wrap(err);
  }
}

export async function listClaimPhotos(jobId: string, context?: RequestContext): Promise<MediaRecord[]> {
  try {
    if (context) {
      const res = await axios.get<MediaRecord[]>(`${context.baseUrl}/${jobId}/photos`, {
        headers: context.headers,
      });
      return res.data;
    }
    const res = await client().get<MediaRecord[]>(`${BASE_PATH}/${jobId}/photos`, {
      headers: tenantHeaders(),
    });
    return res.data;
  } catch (err) {
    throw wrap(err);
  }
}

export async function listClaimVideos(jobId: string, context?: RequestContext): Promise<MediaRecord[]> {
  try {
    if (context) {
      const res = await axios.get<MediaRecord[]>(`${context.baseUrl}/${jobId}/videos`, {
        headers: context.headers,
      });
      return res.data;
    }
    const res = await client().get<MediaRecord[]>(`${BASE_PATH}/${jobId}/videos`, {
      headers: tenantHeaders(),
    });
    return res.data;
  } catch (err) {
    throw wrap(err);
  }
}

export async function uploadClaimPhoto(
  jobId: string,
  file: File,
  photoType: string,
  context?: RequestContext,
): Promise<MediaRecord> {
  try {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('photo_type', photoType);
    if (context) {
      const res = await axios.post<MediaRecord>(`${context.baseUrl}/${jobId}/photos`, formData, {
        headers: { ...context.headers, 'Content-Type': 'multipart/form-data' },
      });
      return res.data;
    }
    const res = await client().post<MediaRecord>(`${BASE_PATH}/${jobId}/photos`, formData, {
      headers: { ...tenantHeaders(), 'Content-Type': 'multipart/form-data' },
    });
    return res.data;
  } catch (err) {
    throw wrap(err);
  }
}

export async function uploadClaimVideo(
  jobId: string,
  file: File,
  videoType: string,
  context?: RequestContext,
): Promise<MediaRecord> {
  try {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('video_type', videoType);
    if (context) {
      const res = await axios.post<MediaRecord>(`${context.baseUrl}/${jobId}/videos`, formData, {
        headers: { ...context.headers, 'Content-Type': 'multipart/form-data' },
      });
      return res.data;
    }
    const res = await client().post<MediaRecord>(`${BASE_PATH}/${jobId}/videos`, formData, {
      headers: { ...tenantHeaders(), 'Content-Type': 'multipart/form-data' },
    });
    return res.data;
  } catch (err) {
    throw wrap(err);
  }
}

export async function deleteClaimPhoto(jobId: string, photoId: string, context?: RequestContext): Promise<void> {
  try {
    if (context) {
      await axios.delete(`${context.baseUrl}/${jobId}/photos/${photoId}`, {
        headers: context.headers,
      });
      return;
    }
    await client().delete(`${BASE_PATH}/${jobId}/photos/${photoId}`, {
      headers: tenantHeaders(),
    });
  } catch (err) {
    throw wrap(err);
  }
}

export async function deleteClaimVideo(jobId: string, videoId: string, context?: RequestContext): Promise<void> {
  try {
    if (context) {
      await axios.delete(`${context.baseUrl}/${jobId}/videos/${videoId}`, {
        headers: context.headers,
      });
      return;
    }
    await client().delete(`${BASE_PATH}/${jobId}/videos/${videoId}`, {
      headers: tenantHeaders(),
    });
  } catch (err) {
    throw wrap(err);
  }
}

// ── Bulk downloads (Download popup on the claims list) ──────────────────────

// The shared client() has a 30s timeout tuned for normal JSON calls. These
// operations are inherently slow — a ZIP built from every photo/document
// pulled out of object storage, or a weasyprint PDF render — and routinely
// run past 30s on claims with many photos (the "timeout of 30000ms exceeded"
// error on the Download popup). Give them a 5-minute ceiling instead.
const LONG_DOWNLOAD_TIMEOUT_MS = 300_000;

export async function downloadClaimMediaZip(jobId: string): Promise<Blob> {
  try {
    const res = await client().get<Blob>(`${BASE_PATH}/${jobId}/media/download`, {
      headers: tenantHeaders(),
      responseType: 'blob',
      timeout: LONG_DOWNLOAD_TIMEOUT_MS,
    });
    return res.data;
  } catch (err) {
    throw wrap(err);
  }
}

export async function downloadClaimDocumentsZip(jobId: string): Promise<Blob> {
  try {
    const res = await client().get<Blob>(`${BASE_PATH}/${jobId}/documents/download`, {
      headers: tenantHeaders(),
      responseType: 'blob',
      timeout: LONG_DOWNLOAD_TIMEOUT_MS,
    });
    return res.data;
  } catch (err) {
    throw wrap(err);
  }
}

export async function generateSurveyReportPdf(jobId: string): Promise<Blob> {
  try {
    const res = await client().post<Blob>(`${BASE_PATH}/${jobId}/survey-report/generate`, undefined, {
      headers: tenantHeaders(),
      responseType: 'blob',
      timeout: LONG_DOWNLOAD_TIMEOUT_MS,
    });
    return res.data;
  } catch (err) {
    throw wrap(err);
  }
}

export async function generateBillInvoicePdf(jobId: string): Promise<Blob> {
  try {
    const res = await client().post<Blob>(`${BASE_PATH}/${jobId}/bill-invoice/generate`, undefined, {
      headers: tenantHeaders(),
      responseType: 'blob',
      timeout: LONG_DOWNLOAD_TIMEOUT_MS,
    });
    return res.data;
  } catch (err) {
    throw wrap(err);
  }
}


export interface ReminderLogEntry {
  stage: number;
  status: 'sent' | 'failed' | 'skipped';
  whatsapp_number: string | null;
  template_name: string | null;
  message: string | null;
  error_message: string | null;
  created_at: string | null;
}

export async function getReminderLogs(jobId: string): Promise<ReminderLogEntry[]> {
  try {
    const res = await client().get<ReminderLogEntry[]>(`${BASE_PATH}/${jobId}/reminder-logs`, {
      headers: tenantHeaders(),
    });
    return res.data;
  } catch (err) {
    throw wrap(err);
  }
}
